import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';
import useOnLoad from '~/hooks/useOnLoad';

export enum LoadPriority {
	FIRST_PAINT = 'paint',
	ON_LOAD = 'load',
	LOW_PRIORITY = 'low',
}

type QueuedPriority = LoadPriority.ON_LOAD | LoadPriority.LOW_PRIORITY;
const queuedPriorities = Object.values(LoadPriority).filter(
	(priority): priority is QueuedPriority => priority !== LoadPriority.FIRST_PAINT,
);

interface QueueState {
	items: string[];
	ready: Record<string, true>;
}

interface State {
	queues: Record<QueuedPriority, QueueState>;
	register: (priority: LoadPriority, id: string) => void;
	unregister: (priority: LoadPriority, id: string) => void;
}

const empty = (): State => ({
	queues: {
		[LoadPriority.ON_LOAD]: { items: [], ready: {} },
		[LoadPriority.LOW_PRIORITY]: { items: [], ready: {} },
	},
	register: () => {},
	unregister: () => {},
});

const context = createContext<State>(empty());

export function LoadPriorityProvider({ children }: { children: React.ReactNode }) {
	const loaded = useOnLoad();
	const [queues, setQueues] = useState<State['queues']>(empty().queues);
	const pendingItems = useMemo(
		() =>
			queuedPriorities.reduce((total, priority) => total + queues[priority].items.filter((id) => !queues[priority].ready[id]).length, 0),
		[queues],
	);

	useEffect(() => {
		if (!loaded || pendingItems === 0) {
			return;
		}

		setQueues((current) => {
			for (const priority of queuedPriorities) {
				const queue = current[priority];

				const nextItems = queue.items.filter((id) => !queue.ready[id]).slice(0, 5);

				if (nextItems.length === 0) {
					continue;
				}

				return {
					...current,
					[priority]: {
						...queue,
						ready: markReady(queue.ready, nextItems),
					},
				};
			}

			return current;
		});
	}, [loaded, pendingItems]);

	const register = useCallback((priority: LoadPriority, id: string) => {
		if (priority === LoadPriority.FIRST_PAINT) {
			return;
		}

		setQueues((current) => {
			const queue = current[priority];

			if (queue.items.includes(id)) {
				return current;
			}

			return {
				...current,
				[priority]: {
					...queue,
					items: [...queue.items, id],
				},
			};
		});
	}, []);

	const unregister = useCallback((priority: LoadPriority, id: string) => {
		if (priority === LoadPriority.FIRST_PAINT) {
			return;
		}

		setQueues((current) => {
			const queue = current[priority];

			if (!queue.items.includes(id) && !queue.ready[id]) {
				return current;
			}

			const { [id]: _removed, ...ready } = queue.ready;

			return {
				...current,
				[priority]: {
					items: queue.items.filter((item) => item !== id),
					ready,
				},
			};
		});
	}, []);

	const value = useMemo<State>(
		() => ({
			queues,
			register,
			unregister,
		}),
		[queues, register, unregister],
	);

	return <context.Provider value={value}>{children}</context.Provider>;
}

export default function useLoadPriority(priority: LoadPriority): boolean {
	const ref = useRef(v4());
	const { queues, register, unregister } = use(context);

	useEffect(() => {
		register(priority, ref.current);

		return () => unregister(priority, ref.current);
	}, [priority, register, unregister]);

	if (priority === LoadPriority.FIRST_PAINT) {
		return true;
	}

	return Boolean(queues[priority].ready[ref.current]);
}

function markReady(ready: Record<string, true>, ids: string[]) {
	if (ids.length === 0) {
		return ready;
	}

	const next = { ...ready };

	for (const id of ids) {
		next[id] = true;
	}

	return next;
}
